import React from 'react';
import PublicLayout from '../../components/public/PublicLayout';
import { Newspaper, Calendar, Download } from 'lucide-react';

const PressReleasesPage = () => {
  const pressReleases = [
    {
      title: 'ARCA Launches Pan-African Respiratory Care Initiative',
      date: 'November 2024',
      category: 'Program Launch',
      excerpt: 'The Africa Respiratory Care Association announces the launch of a comprehensive initiative to strengthen respiratory care services across the continent.',
      status: 'Coming Soon'
    },
    {
      title: 'ARCA Virtual University Reaches 1,000 Students',
      date: 'October 2024',
      category: 'Milestone',
      excerpt: 'ARCA Virtual University celebrates a major milestone as enrollment surpasses 1,000 respiratory therapy students from 25 African countries.',
      status: 'Coming Soon'
    },
    {
      title: 'New Clinical Guidelines for Mechanical Ventilation Released',
      date: 'September 2024',
      category: 'Clinical Updates',
      excerpt: 'ARCA releases evidence-based guidelines for mechanical ventilation management adapted for African healthcare settings.',
      status: 'Coming Soon'
    },
    {
      title: 'Partnership Announced with Global Respiratory Alliance',
      date: 'August 2024',
      category: 'Partnerships',
      excerpt: 'ARCA enters strategic partnership to enhance respiratory therapy education and equipment access across Africa.',
      status: 'Coming Soon'
    },
    {
      title: 'Annual Conference 2024 Registration Opens',
      date: 'July 2024',
      category: 'Events',
      excerpt: 'Registration is now open for the ARCA Annual Conference, featuring workshops, presentations, and networking opportunities.',
      status: 'Coming Soon'
    },
    {
      title: 'COVID-19 Respiratory Care Response Initiative',
      date: 'June 2024',
      category: 'Public Health',
      excerpt: 'ARCA launches emergency response training program for respiratory therapists supporting COVID-19 patient care.',
      status: 'Coming Soon'
    }
  ];

  const categories = ['All', 'Program Launch', 'Milestone', 'Clinical Updates', 'Partnerships', 'Events', 'Public Health'];
  const [selectedCategory, setSelectedCategory] = React.useState('All');

  const filteredReleases = selectedCategory === 'All'
    ? pressReleases
    : pressReleases.filter(release => release.category === selectedCategory);

  return (
    <PublicLayout>
      <div className="py-5">
        <div className="container">
          {/* Header */}
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                style={{ width: '80px', height: '80px', backgroundColor: '#6B8E23' }}>
                <Newspaper className="text-white" size={40} />
              </div>
              <h1 className="display-4 fw-bold text-dark mb-3">Press Releases</h1>
              <p className="lead text-muted">
                Official announcements and news from the Africa Respiratory Care Association
              </p>
            </div>
          </div>

          {/* Category Filter */}
          <div className="row justify-content-center mb-4">
            <div className="col-lg-10">
              <div className="d-flex flex-wrap justify-content-center gap-2">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`btn btn-sm ${selectedCategory === category
                        ? 'text-white'
                        : 'btn-outline-secondary'
                      }`}
                    style={selectedCategory === category ? { backgroundColor: '#6B8E23', borderColor: '#6B8E23' } : {}}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Press Releases List */}
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="row g-4 mb-5">
                {filteredReleases.map((release, index) => (
                  <div key={index} className="col-12">
                    <div className="card shadow-sm border-0">
                      <div className="card-body p-4">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start mb-3">
                          <div className="flex-grow-1 mb-3 mb-md-0">
                            <div className="d-flex align-items-center mb-2">
                              <Calendar size={16} className="me-2" style={{ color: '#6B8E23' }} />
                              <span className="small text-muted">{release.date}</span>
                              <span className="badge text-white ms-3" style={{ backgroundColor: '#6B8E23' }}>
                                {release.category}
                              </span>
                            </div>
                            <h4 className="fw-bold text-dark mb-2">{release.title}</h4>
                            <p className="text-muted mb-0">{release.excerpt}</p>
                          </div>
                          <div className="d-flex gap-2 flex-shrink-0">
                            <span className="badge bg-warning text-dark">{release.status}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Media Contact Section */}
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-5">
                  <h3 className="h4 fw-bold text-dark mb-4">Media Inquiries</h3>
                  <div className="row g-4">
                    <div className="col-md-6">
                      <h5 className="fw-bold text-dark mb-3">
                        <i className="fas fa-envelope me-2" style={{ color: '#6B8E23' }}></i>
                        Press Contact
                      </h5>
                      <p className="text-muted mb-2">
                        For media inquiries, interview requests, or press information:
                      </p>
                      <p className="text-dark fw-semibold">
                        <a href="mailto:media@africaresp.org" style={{ color: '#6B8E23', textDecoration: 'none' }}>
                          media@africaresp.org
                        </a>
                      </p>
                    </div>
                    <div className="col-md-6">
                      <h5 className="fw-bold text-dark mb-3">
                        <i className="fas fa-newspaper me-2" style={{ color: '#6B8E23' }}></i>
                        Subscribe to Updates
                      </h5>
                      <p className="text-muted mb-2">
                        Subscribe to receive the latest press releases and announcements:
                      </p>
                      <a href="/contact" className="btn text-white" style={{ backgroundColor: '#6B8E23' }}>
                        Subscribe Now
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Archive Notice */}
              <div className="card shadow border-0" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)' }}>
                <div className="card-body p-5 text-center text-white">
                  <h3 className="h4 fw-bold mb-3">Press Releases Archive</h3>
                  <p className="mb-4">
                    Our press release archive is being compiled. Media professionals and members
                    can request specific information or past announcements by contacting our media team.
                  </p>
                  <div className="d-flex flex-wrap justify-content-center gap-3">
                    <a href="/media/media-kit" className="btn btn-light btn-lg fw-bold">View Media Kit</a>
                    <a href="/contact" className="btn btn-outline-light btn-lg">Contact Media Team</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default PressReleasesPage;
